import { QueryClient } from '@tanstack/react-query';

import { useNonBenefitPriceTotalSummaryQuery } from '@/hooks/nonBenefit/nonBenefit.gql.impl';
import { NonBenefitPriceSummary } from '@/models/nonBenefit';
import { NonBenefitPriceTotalSummaryQuery } from '@/types/bff/gql-schema';

const parseNonBenefitPriceTotalSummary = (data: NonBenefitPriceTotalSummaryQuery): NonBenefitPriceSummary => {
  const nationalSummaries = data.nonBenefitPriceTotalSummary.nationalSummaries.map((nationalSummary) => ({
    averagePrice: nationalSummary.averagePrice,
    minPrice: nationalSummary.minPrice,
    treatmentMedicineUnitId: nationalSummary.treatmentMedicineUnitId,
    unitDisplayName: nationalSummary.unitDisplayName,
    unitTypeDisplayName: nationalSummary.unitTypeDisplayName,
  }));
  const regionSummaries = data.nonBenefitPriceTotalSummary.regionSummaries.map((regionSummary) => ({
    averagePrice: regionSummary.averagePrice,
    minPrice: regionSummary.minPrice,
    treatmentMedicineUnitId: regionSummary.treatmentMedicineUnitId,
    unitDisplayName: regionSummary.unitDisplayName,
    unitTypeDisplayName: regionSummary.unitTypeDisplayName,
  }));
  return new NonBenefitPriceSummary({
    regionSummaries,
    nationalSummaries,
  });
};

const useNonBenefitPriceTotalSummary = (treatmentMedicineId: string, regionCode: string) => {
  const {
    data: nonBenefitPriceTotalSummary = new NonBenefitPriceSummary({
      regionSummaries: [],
      nationalSummaries: [],
    }),
  } = useNonBenefitPriceTotalSummaryQuery(
    {
      regionCode,
      treatmentMedicineId,
    },
    {
      select: parseNonBenefitPriceTotalSummary,
    }
  );

  return {
    nonBenefitPriceTotalSummary,
  };
};

export default useNonBenefitPriceTotalSummary;

const prefetchQuery = async (queryClient: QueryClient, treatmentMedicineId: string, regionCode: string) => {
  await queryClient.prefetchQuery(
    useNonBenefitPriceTotalSummaryQuery.getKey({
      regionCode,
      treatmentMedicineId,
    }),
    useNonBenefitPriceTotalSummaryQuery.fetcher({
      regionCode,
      treatmentMedicineId,
    })
  );
};

useNonBenefitPriceTotalSummary.prefetchQuery = prefetchQuery;
